import { stateName } from './india.js';
import { splitGst } from './platform.js';
const INK = '#111827';
const MUTED = '#6b7280';
const LINE = '#e2e5ea';
const money = (amount, currency) => {
    const figure = amount.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
    return currency === 'INR' ? `₹${figure}` : `${currency} ${figure}`;
};
const day = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
    : '—';
const longDay = (iso) => iso
    ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })
    : '—';
/** Escapes text going into HTML. A business name is somebody else's text. */
const esc = (value) => (value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
const ONES = [
    '',
    'One',
    'Two',
    'Three',
    'Four',
    'Five',
    'Six',
    'Seven',
    'Eight',
    'Nine',
    'Ten',
    'Eleven',
    'Twelve',
    'Thirteen',
    'Fourteen',
    'Fifteen',
    'Sixteen',
    'Seventeen',
    'Eighteen',
    'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const under100 = (n) => n < 20 ? (ONES[n] ?? '') : `${TENS[Math.floor(n / 10)]}${n % 10 ? ` ${ONES[n % 10]}` : ''}`;
const under1000 = (n) => n < 100
    ? under100(n)
    : `${ONES[Math.floor(n / 100)]} Hundred${n % 100 ? ` ${under100(n % 100)}` : ''}`;
/**
 * The total written out, which a tax invoice is expected to carry.
 *
 * Grouped the Indian way - crore, lakh, thousand - so 1,25,000 reads as one
 * lakh twenty-five thousand rather than one hundred and twenty-five thousand.
 */
export function rupeesInWords(amount) {
    const absolute = Math.abs(amount);
    const whole = Math.floor(absolute);
    const paise = Math.round((absolute - whole) * 100);
    if (whole === 0 && paise === 0)
        return 'Rupees Zero Only';
    const parts = [];
    const push = (value, label) => {
        if (value > 0)
            parts.push(label ? `${under1000(value)} ${label}` : under1000(value));
    };
    push(Math.floor(whole / 10000000), 'Crore');
    push(Math.floor((whole % 10000000) / 100000), 'Lakh');
    push(Math.floor((whole % 100000) / 1000), 'Thousand');
    push(whole % 1000, '');
    const rupees = parts.join(' ').trim();
    const head = rupees ? `Rupees ${rupees}` : '';
    if (paise > 0) {
        const tail = `${under100(paise)} Paise`;
        return head ? `${head} and ${tail} Only` : `Rupees and ${tail} Only`;
    }
    return `${head} Only`;
}
/* ---------------- the blocks the document is built from ---------------- */
const label = (text) => `<div style="font-size:9.5px;letter-spacing:.07em;color:${MUTED};text-transform:uppercase;font-weight:600">${text}</div>`;
const line = (text) => `<div style="font-size:12px;color:#374151;line-height:1.5">${text}</div>`;
/** One "Label: value" row inside a details panel. */
const detail = (name, value) => `<div style="font-size:12px;line-height:1.6"><span style="color:${MUTED}">${name}:</span> <b style="color:${INK}">${value}</b></div>`;
const partyBlock = (p, heading) => [
    `<td style="vertical-align:top;padding:12px 14px;width:50%;border:1px solid ${LINE}">`,
    label(heading),
    `<div style="font-weight:700;font-size:13px;margin:5px 0 3px">${esc(p.name) || '&mdash;'}</div>`,
    p.address ? line(esc(p.address)) : '',
    p.stateCode ? line(`${esc(stateName(p.stateCode))} &ndash; ${esc(p.stateCode)}`) : '',
    /*
     * GSTIN keeps its row even when there is none.
     *
     * A hotel below the registration threshold genuinely has no GSTIN, and
     * leaving the line out reads as though nobody looked. Showing the label
     * with nothing after it says it was asked for and the answer was none -
     * and keeps the two columns the same height, which is what stopped the
     * blocks sitting at different depths.
     */
    line(`<b>GSTIN:</b> ${esc(p.gstin) || '&mdash;'}`),
    p.email ? line(esc(p.email)) : '',
    p.phone ? line(esc(p.phone)) : '',
    `</td>`,
].join('');
/** A two-column fact table, as under "Payment & Subscription Information". */
const factRows = (rows) => rows
    .map(([name, value]) => `<tr><td style="padding:7px 14px;border:1px solid ${LINE};background:#f8f9fb;font-size:12px;font-weight:600;color:#374151;width:38%">${name}</td>` +
    `<td style="padding:7px 14px;border:1px solid ${LINE};font-size:12px;color:${INK}">${value}</td></tr>`)
    .join('');
const sectionTitle = (text) => `<div style="font-size:12.5px;font-weight:700;font-style:italic;color:${INK};margin:20px 0 8px">${text}</div>`;
/**
 * The invoice as a self-contained block of HTML.
 */
export function renderSubscriptionInvoice(invoice) {
    const cur = invoice.currency;
    /*
     * What was charged, taken from the invoice where it was stored.
     *
     * The split is worked out when an invoice is issued and kept on the row, so
     * a hotel that later moves state cannot restate one already sent. That rule
     * holds for every invoice issued since the columns existed, and not for the
     * ones issued before - which carry three zeroes and a tax amount, and once
     * printed "IGST @ 18%  0.00" on an invoice whose total plainly included it.
     *
     * So where nothing was stored, the split is worked out now from the state
     * codes on the invoice, by the same rule that would have applied then. Only
     * ever a fallback: a stored split always wins.
     */
    const stored = invoice.cgstAmount + invoice.sgstAmount + invoice.igstAmount;
    const split = stored > 0
        ? {
            cgst: invoice.cgstAmount,
            sgst: invoice.sgstAmount,
            igst: invoice.igstAmount,
            placeOfSupply: invoice.placeOfSupply,
        }
        : splitGst(invoice.taxAmount, invoice.seller.stateCode, invoice.buyer.stateCode);
    const intraState = split.cgst > 0 || split.sgst > 0;
    const half = invoice.taxRate / 2;
    const paid = invoice.status === 'PAID';
    const totalRow = (name, value, strong = false) => `<tr><td style="padding:${strong ? '10px' : '6px'} 14px;text-align:right;font-size:${strong ? '14px' : '12.5px'};${strong ? `font-weight:700;border-top:2px solid ${INK}` : `color:${MUTED}`}">${name}</td>` +
        `<td style="padding:${strong ? '10px' : '6px'} 14px;text-align:right;font-size:${strong ? '14px' : '12.5px'};font-weight:${strong ? 700 : 600};${strong ? `border-top:2px solid ${INK}` : ''};white-space:nowrap">${value}</td></tr>`;
    const taxRows = invoice.taxAmount <= 0
        ? totalRow(esc(invoice.taxLabel) || 'Tax', money(0, cur))
        : intraState
            ? totalRow(`CGST @ ${half}%`, money(split.cgst, cur)) +
                totalRow(`SGST @ ${half}%`, money(split.sgst, cur))
            : totalRow(`IGST @ ${invoice.taxRate}%`, money(split.igst, cur));
    const supplyState = invoice.placeOfSupply ?? split.placeOfSupply;
    return [
        `<div style="font-family:system-ui,-apple-system,'Segoe UI',Roboto,sans-serif;color:${INK};max-width:780px;background:#fff">`,
        /* ---- header: who is billing, and what this is ---- */
        `<table style="width:100%;border-collapse:collapse"><tr>`,
        `<td style="vertical-align:top;padding:0 14px 16px 0">`,
        `<div style="font-size:26px;font-weight:800;letter-spacing:.04em">SARTHI</div>`,
        `<div style="font-size:10.5px;color:${MUTED};margin-bottom:8px">Smart Automated Retail Transaction &amp; Hospitality Infrastructure</div>`,
        `<div style="font-size:12.5px;font-weight:700">${esc(invoice.seller.name) || '&mdash;'}</div>`,
        invoice.seller.address ? line(esc(invoice.seller.address)) : '',
        invoice.seller.gstin ? line(`GSTIN: ${esc(invoice.seller.gstin)}`) : '',
        invoice.seller.email ? line(esc(invoice.seller.email)) : '',
        `</td>`,
        `<td style="vertical-align:top;width:300px;background:#f5f7fa;border:1px solid ${LINE};padding:14px">`,
        `<div style="display:block;text-align:right">`,
        `<span style="font-size:20px;font-weight:800;letter-spacing:.03em">TAX INVOICE</span>`,
        paid
            ? `<span style="display:inline-block;margin-left:8px;background:#0f9d58;color:#fff;font-size:10px;font-weight:700;letter-spacing:.08em;padding:3px 8px;border-radius:3px;vertical-align:middle">PAID</span>`
            : '',
        `</div>`,
        `<div style="margin-top:10px">`,
        detail('Invoice No', esc(invoice.number)),
        detail('Invoice Date', day(invoice.issuedAt)),
        detail('Billing Period', `${day(invoice.periodStart)} &ndash; ${day(invoice.periodEnd)}`),
        detail('Payment', esc(invoice.payment.gateway) || 'Online'),
        `</div></td></tr></table>`,
        /* ---- bill to / invoice details ---- */
        `<table style="width:100%;border-collapse:collapse;table-layout:fixed"><tr>`,
        partyBlock(invoice.buyer, 'Bill To'),
        `<td style="vertical-align:top;padding:12px 14px;width:50%;border:1px solid ${LINE}">`,
        label('Invoice Details'),
        `<div style="margin-top:5px">`,
        invoice.customerRef ? detail('Customer ID', esc(invoice.customerRef)) : '',
        invoice.subscriptionRef ? detail('Subscription ID', esc(invoice.subscriptionRef)) : '',
        detail('Plan', esc(invoice.planName)),
        invoice.billingCycle ? detail('Billing Cycle', esc(invoice.billingCycle)) : '',
        detail('Place of Supply', supplyState ? `${esc(stateName(supplyState))} (${esc(supplyState)})` : '&mdash;'),
        detail('Reverse Charge', 'No'),
        detail('Payment Status', paid ? 'Paid' : esc(invoice.status)),
        `</div></td></tr></table>`,
        /* ---- the charge ---- */
        sectionTitle('Subscription Charges'),
        `<table style="width:100%;border-collapse:collapse;table-layout:fixed">`,
        `<thead><tr style="background:${INK};color:#fff">`,
        `<th style="padding:9px 14px;text-align:left;font-size:11.5px;font-weight:600">Description</th>`,
        `<th style="padding:9px 10px;text-align:left;font-size:11.5px;font-weight:600;width:80px">SAC</th>`,
        `<th style="padding:9px 10px;text-align:right;font-size:11.5px;font-weight:600;width:52px">Qty</th>`,
        `<th style="padding:9px 10px;text-align:right;font-size:11.5px;font-weight:600;width:110px">Unit Price</th>`,
        `<th style="padding:9px 14px;text-align:right;font-size:11.5px;font-weight:600;width:120px">Amount</th>`,
        `</tr></thead><tbody><tr>`,
        `<td style="padding:11px 14px;border:1px solid ${LINE};font-size:12.5px">`,
        `<b>SARTHI HMS &ndash; ${esc(invoice.planName)} Plan</b>`,
        `<div style="font-size:11px;color:${MUTED};margin-top:2px">${esc(invoice.billingCycle) || 'Subscription'} &middot; ${day(invoice.periodStart)} to ${day(invoice.periodEnd)} &middot; cycle ${invoice.cycleNumber}</div>`,
        `</td>`,
        `<td style="padding:11px 10px;border:1px solid ${LINE};font-size:12px">${esc(invoice.sacCode) || '&mdash;'}</td>`,
        `<td style="padding:11px 10px;border:1px solid ${LINE};font-size:12.5px;text-align:right">1</td>`,
        `<td style="padding:11px 10px;border:1px solid ${LINE};font-size:12.5px;text-align:right;white-space:nowrap">${money(invoice.subtotal, cur)}</td>`,
        `<td style="padding:11px 14px;border:1px solid ${LINE};font-size:12.5px;text-align:right;white-space:nowrap">${money(invoice.subtotal, cur)}</td>`,
        `</tr></tbody></table>`,
        /* ---- totals ---- */
        `<table style="width:330px;border-collapse:collapse;margin:10px 0 0 auto">`,
        totalRow('Subtotal', money(invoice.subtotal, cur)),
        taxRows,
        totalRow('TOTAL', money(invoice.total, cur), true),
        `</table>`,
        `<table style="width:100%;border-collapse:collapse;margin-top:12px">`,
        `<tr><td style="padding:9px 14px;border:1px solid ${LINE};background:#eaf7ef;font-size:12.5px;font-weight:700">Amount Paid</td>`,
        `<td style="padding:9px 14px;border:1px solid ${LINE};background:#eaf7ef;font-size:12.5px;font-weight:700;text-align:right">${money(paid ? invoice.total : 0, cur)}</td></tr>`,
        `<tr><td style="padding:9px 14px;border:1px solid ${LINE};font-size:12.5px">Balance Due</td>`,
        `<td style="padding:9px 14px;border:1px solid ${LINE};font-size:12.5px;font-weight:700;text-align:right">${money(paid ? 0 : invoice.total, cur)}</td></tr>`,
        `</table>`,
        /* ---- how it was paid ---- */
        sectionTitle('Payment &amp; Subscription Information'),
        `<table style="width:100%;border-collapse:collapse">`,
        factRows([
            ['Payment Gateway', esc(invoice.payment.gateway) || '—'],
            invoice.payment.transactionId
                ? ['Transaction ID', esc(invoice.payment.transactionId)]
                : null,
            ['Payment Date', longDay(invoice.payment.paidAt ?? invoice.issuedAt)],
            ['Subscription Start', longDay(invoice.periodStart)],
            ['Subscription End', longDay(invoice.periodEnd)],
            ['Auto Renewal', invoice.autoRenew ? 'Enabled' : 'Not enabled'],
        ].filter(Boolean)),
        `</table>`,
        `<div style="font-size:12.5px;margin-top:14px"><b>Amount in Words:</b> ${esc(rupeesInWords(invoice.total))}</div>`,
        /* ---- terms ---- */
        `<div style="margin-top:16px">`,
        `<div style="font-size:11px;font-weight:700;color:${MUTED};letter-spacing:.04em">NOTES &amp; TERMS</div>`,
        `<div style="font-size:11px;color:${MUTED};line-height:1.6;margin-top:4px">`,
        `This invoice is generated electronically and does not require a physical signature. `,
        `Subscription fees are governed by the SARTHI Terms of Service and applicable refund policy. `,
        `The customer is responsible for ensuring that GST and billing details are accurate.`,
        `</div></div>`,
        `<table style="width:100%;border-collapse:collapse;margin-top:16px;border-top:1px solid ${LINE}"><tr>`,
        `<td style="padding-top:10px;font-size:11.5px;vertical-align:top">`,
        `<b>Thank you for choosing SARTHI.</b>`,
        `<div style="color:${MUTED};margin-top:2px">SARTHI &ndash; Smart Automated Retail Transaction &amp; Hospitality Infrastructure</div>`,
        `</td>`,
        `<td style="padding-top:10px;font-size:11.5px;text-align:right;vertical-align:top">`,
        `<b>For billing support</b>`,
        `<div style="color:${MUTED};margin-top:2px">${esc(invoice.seller.email) || '&mdash;'}</div>`,
        `</td></tr></table>`,
        `</div>`,
    ].join('');
}
//# sourceMappingURL=subscription-invoice.js.map